import { useCallback } from "react";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import type { RoleRef, Workspace } from "../../api/types";

/** Built-in roles are shown in the UI language; custom roles by their own name. */
export function useRoleLabel(): (role: Pick<RoleRef, "name" | "builtin" | "preset">) => string {
  const { t } = useI18n();
  return useCallback(
    (role) =>
      role.builtin
        ? t(`roles.builtin.${role.builtin}` as MessageKey)
        : role.preset
          ? t(`team.staff.preset.${role.preset}` as MessageKey)
          : role.name,
    [t],
  );
}

const BUILTIN_ROLE_KEYS: ReadonlySet<string> = new Set([
  "owner",
  "admin",
  "agent",
  "builder",
  "marketer",
  "developer",
  "viewer",
]);

/** Text that is just a built-in role key (a member name, an audit value) shows the translated role. */
export function useBuiltinRoleText(): (text: string) => string {
  const { t } = useI18n();
  return useCallback(
    (text) => (BUILTIN_ROLE_KEYS.has(text) ? t(`roles.builtin.${text}` as MessageKey) : text),
    [t],
  );
}

/**
 * UI hint only: whether the signed-in member holds `permission` in this workspace. The API
 * re-checks every request (ADR-0017), so hiding a control is never the protection.
 */
export function hasPermission(workspace: Workspace, permission: string): boolean {
  return workspace.permissions?.granted?.includes(permission) ?? false;
}

/**
 * UI gate for pages/controls that the API protects anyway: allowed when the workspace payload
 * carries no `granted` list at all (older API builds), otherwise requires the permission.
 */
export function mayUse(workspace: Workspace, permission: string): boolean {
  const granted = workspace.permissions?.granted;
  return granted === undefined ? true : granted.includes(permission);
}

/** Locale-aware short date (Latin digits in Arabic, like the rest of the UI). */
export function formatDate(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-u-nu-latn" : "en", {
    dateStyle: "medium",
  }).format(date);
}
